import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, FormControl, Select, MenuItem, Switch, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'sonner';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import { api } from '../api/fetchWrapper';
import { getEndpoint, API_ENDPOINTS } from '../api/apiConstants';

export interface JobDetailDataProps {
  clientCode: string;
  jobId: string;
  onJobNameChange?: (name: string) => void;
}

export interface JobDetailDataRef {
  save: () => Promise<void>;
  toggleEdit: () => void;
  startJob: () => Promise<void>;
  refresh: () => Promise<void>;
  getChangesSummary: () => string[];
  isLoading: () => boolean;
  getDisplayJobName: () => string;
  getClientId: () => number | undefined;
}

const JobDetailData = forwardRef<JobDetailDataRef, JobDetailDataProps>(({ clientCode, jobId, onJobNameChange }, ref) => {
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [jobData, setJobData] = useState<any | null>(null);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [scheduleTypeOptions, setScheduleTypeOptions] = useState<string[]>([]);
  const [intervalTypeOptions, setIntervalTypeOptions] = useState<string[]>([]);

  const [formValues, setFormValues] = useState({
    status: '' as string,
    intervalType: '' as string,
    timeIntervalType: '' as string,
    nextRunTime: 0 as number,
    lastScheduledStartTime: 0 as number,
    retryEnabled: false,
    isAutoRetryEnabled: false,
    agentJob: false,
    interval: 0 as number,
  });

  // For MONTHLY interval, allow user to choose number of months and compute minutes
  const [monthlyMonths, setMonthlyMonths] = useState<number>(1);

  // Track initial form values when data was loaded (for save confirmation diff)
  const initialFormValuesRef = useRef<typeof formValues | null>(null);

  const formatTimestamp = (timestamp: number): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatLocalInput = (timestamp: number): string => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const displayJobName = useMemo(() => jobData?.jobName || (jobId ? `Job ${jobId}` : 'Job undefined'), [jobData, jobId]);

  useEffect(() => {
    // Only propagate a real job name to avoid flicker back to "Job {id}"
    if (jobData?.jobName) {
      onJobNameChange?.(jobData.jobName);
    }
  }, [jobData?.jobName]);

  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const enumsEndpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_ENUMS);
        const enumsResponse = await api.get<any>(enumsEndpoint);
        if (Array.isArray(enumsResponse)) {
          setStatusOptions(Array.isArray(enumsResponse[0]) ? enumsResponse[0] : []);
          setScheduleTypeOptions(Array.isArray(enumsResponse[1]) ? enumsResponse[1] : []);
          setIntervalTypeOptions(Array.isArray(enumsResponse[2]) ? enumsResponse[2] : []);
        }
      } catch {}
    };
    fetchEnums();
  }, [clientCode]);

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!jobId) return;
        const idNum = parseInt(jobId);
        const directEndpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_BY_ID.replace('{id}', idNum.toString()));
        const response = await api.get<any>(directEndpoint);
        if (response && response.id) {
          const processed = {
            id: response.id || idNum,
            jobName: response.jobName || `Job ${idNum}`,
            jobCode: response.jobCode || `JOB_${idNum}`,
            status: response.status || 'STOPPED',
            interval: response.interval || 0,
            lastStartTime: response.lastStartTime || 0,
            lastScheduledStartTime: response.lastScheduledStartTime || 0,
            lastEndTime: response.lastEndTime || 0,
            nextRunTime: response.nextRunTime || 0,
            retryEnabled: response.retryEnabled || false,
            isAutoRetryEnabled: response.isAutoRetryEnabled || false,
            intervalType: response.intervalType || 'INTERVAL_AFTER_LAST_FINISH',
            timeIntervalType: response.timeIntervalType || 'RELATIVE',
            agentJob: response.agentJob || false,
            lastConfiguredBy: response.lastConfiguredBy || 'System',
            creationDate: response.creationDate || Date.now(),
            lastUpdateDate: response.lastUpdateDate || Date.now(),
            clientId: response.clientId,
            createdBy: response.createdBy,
            lastUpdatedBy: response.lastUpdatedBy,
            version: response.version,
            toDateTime: response.toDateTime ?? null,
            maxRetryCount: response.maxRetryCount ?? null,
          };
          setJobData(processed);
          const fv = {
            status: processed.status,
            intervalType: processed.intervalType,
            timeIntervalType: processed.timeIntervalType,
            nextRunTime: processed.nextRunTime,
            lastScheduledStartTime: processed.lastScheduledStartTime,
            retryEnabled: processed.retryEnabled,
            isAutoRetryEnabled: processed.isAutoRetryEnabled,
            agentJob: processed.agentJob,
            interval: processed.interval,
          };
          setFormValues(fv);
          initialFormValuesRef.current = { ...fv };
          // Default months based on existing interval (~30 days per month)
          if (processed.timeIntervalType === 'MONTHLY' && processed.interval) {
            const approxMonths = Math.max(1, Math.round(processed.interval / (60 * 24 * 30)));
            setMonthlyMonths(approxMonths);
          }
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load job details');
      } finally {
        setLoading(false);
      }
    };
    fetchJobData();
  }, [clientCode, jobId]);

  const handleChangeField = (field: 'status' | 'intervalType' | 'timeIntervalType' | 'nextRunTime' | 'lastScheduledStartTime' | 'retryEnabled' | 'isAutoRetryEnabled' | 'agentJob' | 'interval', value: string | number | boolean) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    if (!jobData) return;
    const normalizeTimestamp = (ts?: number | null) => (ts && ts > 0 ? ts : null);
    // Ensure interval is computed for MONTHLY before saving
    let intervalToSave = formValues.interval;
    if (formValues.timeIntervalType === 'MONTHLY') {
      const baseTs = formValues.lastScheduledStartTime || formValues.nextRunTime || jobData?.lastScheduledStartTime || Date.now();
      const start = new Date(baseTs || Date.now());
      const end = new Date(start.getTime());
      end.setMonth(end.getMonth() + (monthlyMonths || 1));
      intervalToSave = Math.round((end.getTime() - start.getTime()) / 60000);
    }
    const payload = {
      createdBy: jobData.createdBy ?? null,
      creationDate: jobData.creationDate,
      lastUpdateDate: Date.now(),
      lastUpdatedBy: jobData.lastUpdatedBy ?? null,
      clientId: jobData.clientId ?? null,
      id: jobData.id,
      jobName: jobData.jobName,
      lastConfiguredBy: jobData.lastConfiguredBy,
      lastStartTime: normalizeTimestamp(jobData.lastStartTime),
      lastScheduledStartTime: normalizeTimestamp(formValues.lastScheduledStartTime),
      lastEndTime: normalizeTimestamp(jobData.lastEndTime),
      nextRunTime: normalizeTimestamp(formValues.nextRunTime),
      retryEnabled: formValues.retryEnabled,
      maxRetryCount: jobData.maxRetryCount ?? null,
      isAutoRetryEnabled: formValues.isAutoRetryEnabled,
      version: jobData.version ?? null,
      intervalType: formValues.intervalType,
      timeIntervalType: formValues.timeIntervalType,
      interval: intervalToSave,
      toDateTime: jobData.toDateTime ?? null,
      status: formValues.status,
      jobCode: jobData.jobCode,
      agentJob: formValues.agentJob,
    };
    const putEndpoint = getEndpoint(clientCode, 'jobDetailRest/');
    const result = await api.put<any>(putEndpoint, payload);
    if (result?.success) {
      // Re-fetch to sync
      const refreshed = await api.get<any>(getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_BY_ID.replace('{id}', String(jobData.id))));
      if (refreshed) {
        setJobData({ ...jobData, ...refreshed });
      }
      // Sync initial snapshot so "changes" summary is empty until user edits again
      initialFormValuesRef.current = { ...formValues };
      if (formValues.timeIntervalType === 'MONTHLY') {
        const baseTs = formValues.lastScheduledStartTime || formValues.nextRunTime || jobData?.lastScheduledStartTime || Date.now();
        const start = new Date(baseTs || Date.now());
        const end = new Date(start.getTime());
        end.setMonth(end.getMonth() + (monthlyMonths || 1));
        initialFormValuesRef.current = { ...initialFormValuesRef.current, interval: Math.round((end.getTime() - start.getTime()) / 60000) };
      }
      toast.success('Job details saved successfully');
    }
  };

  const toggleEdit = () => setEditMode(prev => !prev);

  const startJob = async (): Promise<void> => {
    if (!jobId) return;
    // GET /projectag/admin/invoke/{jobid} - response "ok" means job triggered
    const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOB_DETAIL_EDIT.INVOKE_GET}/${jobId}`);
    const response = await api.get<string | { ok?: boolean; success?: boolean }>(endpoint);
    const ok =
      response === 'ok' ||
      (typeof response === 'object' && (response?.ok === true || response?.success === true));
    if (response !== undefined && response !== null && !ok) {
      throw new Error('Job start did not return success.');
    }
  };

  const refresh = async (): Promise<void> => {
    if (!jobId) return;
    try {
      setLoading(true);
      setError(null);
      const idNum = parseInt(jobId);
      const directEndpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_BY_ID.replace('{id}', idNum.toString()));
      const response = await api.get<any>(directEndpoint);
      if (response && response.id) {
        const processed = {
          id: response.id || idNum,
          jobName: response.jobName || `Job ${idNum}`,
          jobCode: response.jobCode || `JOB_${idNum}`,
          status: response.status || 'STOPPED',
          interval: response.interval || 0,
          lastStartTime: response.lastStartTime || 0,
          lastScheduledStartTime: response.lastScheduledStartTime || 0,
          lastEndTime: response.lastEndTime || 0,
          nextRunTime: response.nextRunTime || 0,
          retryEnabled: response.retryEnabled || false,
          isAutoRetryEnabled: response.isAutoRetryEnabled || false,
          intervalType: response.intervalType || 'INTERVAL_AFTER_LAST_FINISH',
          timeIntervalType: response.timeIntervalType || 'RELATIVE',
          agentJob: response.agentJob || false,
          lastConfiguredBy: response.lastConfiguredBy || 'System',
          creationDate: response.creationDate || Date.now(),
          lastUpdateDate: response.lastUpdateDate || Date.now(),
          clientId: response.clientId,
          createdBy: response.createdBy,
          lastUpdatedBy: response.lastUpdatedBy,
          version: response.version,
          toDateTime: response.toDateTime ?? null,
          maxRetryCount: response.maxRetryCount ?? null,
        };
        setJobData(processed);
        const fv = {
          status: processed.status,
          intervalType: processed.intervalType,
          timeIntervalType: processed.timeIntervalType,
          nextRunTime: processed.nextRunTime,
          lastScheduledStartTime: processed.lastScheduledStartTime,
          retryEnabled: processed.retryEnabled,
          isAutoRetryEnabled: processed.isAutoRetryEnabled,
          agentJob: processed.agentJob,
          interval: processed.interval,
        };
        setFormValues(fv);
        initialFormValuesRef.current = { ...fv };
        if (processed.timeIntervalType === 'MONTHLY' && processed.interval) {
          const approxMonths = Math.max(1, Math.round(processed.interval / (60 * 24 * 30)));
          setMonthlyMonths(approxMonths);
        }
        toast.success('Job details refreshed');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to refresh job details');
      toast.error('Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  const getChangesSummary = (): string[] => {
    const initial = initialFormValuesRef.current;
    if (!initial) return [];
    const labels: Record<keyof typeof formValues, string> = {
      status: 'Status',
      intervalType: 'Schedule Type',
      timeIntervalType: 'Interval Type',
      nextRunTime: 'Next Run Time',
      lastScheduledStartTime: 'Last Scheduled Start Time',
      retryEnabled: 'Retry Enabled',
      isAutoRetryEnabled: 'Auto Retry',
      agentJob: 'Agent Job',
      interval: 'Interval (minutes)',
    };
    const changed: string[] = [];
    (Object.keys(labels) as (keyof typeof formValues)[]).forEach((key) => {
      const a = initial[key];
      const b = formValues[key];
      if (a !== b) changed.push(labels[key]);
    });
    if (formValues.timeIntervalType === 'MONTHLY' && initial.timeIntervalType === 'MONTHLY' && monthlyMonths !== Math.max(1, Math.round((initial.interval || 0) / (60 * 24 * 30)))) {
      if (!changed.includes('Interval (minutes)')) changed.push('Interval (minutes)');
    }
    return changed;
  };

  useImperativeHandle(ref, () => ({
    save,
    toggleEdit,
    startJob,
    refresh,
    getChangesSummary,
    isLoading: () => loading,
    getDisplayJobName: () => displayJobName,
    getClientId: () => jobData?.clientId,
  }));

  if (!jobData) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography color="text.secondary">{loading ? 'Loading...' : error || 'No data'}</Typography>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card 
      sx={{ 
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        backgroundColor: SAP_COLORS.sapWhite,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        // Neutralize default MUI blue focus and use SAP gray tones
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: SAP_COLORS.sapGray5,
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: SAP_COLORS.sapGray7,
        },
        flex: 1,
        overflow: 'auto',
      }}>
        {/* Top-right: Refresh + Last Configured By */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography
            sx={{
              color: SAP_COLORS.sapGray6,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
            }}
          >
            Last Configured By: {jobData.lastConfiguredBy}
          </Typography>
          <Tooltip title="Refresh job details">
            <IconButton
              size="small"
              onClick={() => refresh()}
              disabled={loading}
              sx={{
                color: SAP_COLORS.sapBlue,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Top read-only trio: Job Code, Last Start, Last End */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Job Code</Typography>
            <TextField value={jobData.jobCode} disabled fullWidth size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Last Start Time</Typography>
            <TextField value={formatTimestamp(jobData.lastStartTime)} disabled fullWidth size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Last End Time</Typography>
            <TextField value={formatTimestamp(jobData.lastEndTime)} disabled fullWidth size="small" />
          </Box>
        </Box>

        {/* Editable form grid: 2 columns x 3 rows (md+) */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: { xs: 3, md: 6 },
            rowGap: 3,
          }}
        >
          {/* Row 1 */}
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Status</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={formValues.status}
                disabled={!editMode}
                onChange={(e) => handleChangeField('status', String(e.target.value))}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Please select
                </MenuItem>
                {statusOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Interval Type</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={formValues.timeIntervalType}
                disabled={!editMode}
                onChange={(e) => handleChangeField('timeIntervalType', String(e.target.value))}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Please select
                </MenuItem>
                {intervalTypeOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Row 2 */}
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Schedule Type</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={formValues.intervalType}
                disabled={!editMode}
                onChange={(e) => handleChangeField('intervalType', String(e.target.value))}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Please select
                </MenuItem>
                {scheduleTypeOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Interval (minutes)</Typography>
            {formValues.timeIntervalType === 'MONTHLY' ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={monthlyMonths}
                    disabled={!editMode}
                    onChange={(e) => setMonthlyMonths(Number(e.target.value))}
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <MenuItem key={m} value={m}>{m} month{m>1 ? 's' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  value={(() => {
                    const baseTs = formValues.lastScheduledStartTime || formValues.nextRunTime || jobData?.lastScheduledStartTime || Date.now();
                    const start = new Date(baseTs || Date.now());
                    const end = new Date(start.getTime());
                    end.setMonth(end.getMonth() + (monthlyMonths || 1));
                    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
                    return `${minutes}`;
                  })()}
                  helperText={`Monthly interval: ${monthlyMonths} month(s) → computed in minutes`}
                />
              </Box>
            ) : (
              <TextField
                type="number"
                size="small"
                fullWidth
                disabled={!editMode}
                value={formValues.interval}
                onChange={(e) => handleChangeField('interval', Number(e.target.value))}
                inputProps={{ min: 0 }}
              />
            )}
          </Box>

          {/* Row 3 */}
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Next Run Time</Typography>
            <TextField
              type="datetime-local"
              size="small"
              fullWidth
              disabled={!editMode}
              value={formatLocalInput(formValues.nextRunTime || 0)}
              onChange={(e) => handleChangeField('nextRunTime', e.target.value ? new Date(e.target.value).getTime() : 0)}
              inputProps={{ step: 1 }}
              helperText="Pick or type date and time (YYYY-MM-DD HH:mm:ss)"
            />
          </Box>

          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>Last Scheduled Start Time</Typography>
            <TextField
              type="datetime-local"
              size="small"
              fullWidth
              disabled={!editMode}
              value={formatLocalInput(formValues.lastScheduledStartTime || 0)}
              onChange={(e) => handleChangeField('lastScheduledStartTime', e.target.value ? new Date(e.target.value).getTime() : 0)}
              inputProps={{ step: 1 }}
              helperText="Pick or type date and time (YYYY-MM-DD HH:mm:ss)"
            />
          </Box>
        </Box>

        {/* Toggles row */}
        <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Box sx={{ p: 2, border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontWeight={SAP_TYPOGRAPHY.fontWeight.medium}>Retry Enabled</Typography>
            <FormControlLabel
              control={<Switch checked={formValues.retryEnabled} onChange={(e) => handleChangeField('retryEnabled', e.target.checked)} disabled={!editMode} />}
              label={formValues.retryEnabled ? 'Yes' : 'No'}
            />
          </Box>
          <Box sx={{ p: 2, border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontWeight={SAP_TYPOGRAPHY.fontWeight.medium}>Auto Retry</Typography>
            <FormControlLabel
              control={<Switch checked={formValues.isAutoRetryEnabled} onChange={(e) => handleChangeField('isAutoRetryEnabled', e.target.checked)} disabled={!editMode} />}
              label={formValues.isAutoRetryEnabled ? 'Yes' : 'No'}
            />
          </Box>
          <Box sx={{ p: 2, border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontWeight={SAP_TYPOGRAPHY.fontWeight.medium}>Agent Job</Typography>
            <FormControlLabel
              control={<Switch checked={formValues.agentJob} onChange={(e) => handleChangeField('agentJob', e.target.checked)} disabled={!editMode} />}
              label={formValues.agentJob ? 'Yes' : 'No'}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

export default JobDetailData;